import { useTranslation } from 'react-i18next';

import { APPLE_PAY_VERSION } from './ApplePayIdentifierUtil';
import { useCallback } from 'react';
import { useMerchant } from 'hooks/context/merchant';
import { useOrderTotals } from 'routes/Checkout/context/useOrderTotals';

// https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest
export const DEFAULT_REQUEST_BODY: Readonly<Partial<ApplePayJS.ApplePayPaymentRequest>> = {
  countryCode: 'US',
  currencyCode: 'USD',
  // TODO: use entitlements from response (need mapping)
  supportedNetworks: ['amex', 'discover', 'masterCard', 'visa'],
  merchantCapabilities: ['supports3DS'],
  shippingType: 'storePickup', // or 'delivery'
  requiredShippingContactFields: [
    'name',
    'phone',
    'email',
  ],
  requiredBillingContactFields: [
    'postalAddress',
    'email',
    'name',
  ]
};

export function useCreateApplePaySession() {
  const { t } = useTranslation();
  const { merchant } = useMerchant();
  const { tip, totalWithSurcharge, subtotal, tax } = useOrderTotals();

  return useCallback((options: Partial<ApplePayJS.ApplePayPaymentRequest> = {}) => {

    const {
      lineItems = [],
      total: totalOverride = {},
      ...overrides
    } = options;

    return new ApplePaySession(APPLE_PAY_VERSION, {
      ...(DEFAULT_REQUEST_BODY as ApplePayJS.ApplePayPaymentRequest),
      lineItems: [
        ...lineItems,
        { label: t('checkoutPage.subtotal'), type: 'final', amount: subtotal.toFixed(2) },
        tax && { label: t('checkoutPage.tax'), type: 'final', amount: tax.toFixed(2) },
        tip === 0 ? undefined : { label: t('checkoutPage.tip'), type: 'final', amount: tip.toFixed(2) },
      ].filter(o => !!o) as ApplePayJS.ApplePayLineItem[],
      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/1916119-total
      total: {
        ...totalOverride,
        label: merchant?.name ?? t('checkoutPage.total'),
        type: 'final',
        amount: totalWithSurcharge.toFixed(2),
      },
      ...overrides
    });
  }, [merchant, subtotal, tax, tip, totalWithSurcharge, t]);
}
